import { useState } from 'react';
import { useT } from '@/store';
import { useBatchAssign } from '@/hooks/useOrders';
import { Panel, ErrorState } from '@/components/ui/Panel';

/**
 * Rytas planuojamas kartu — the whole set of unassigned loads given drivers
 * as ONE plan, instead of one load at a time.
 *
 * WHY THIS IS TWO CLICKS AND NOT ONE. The first shows the proposal and what
 * it saves against the way the product assigns today; the second commits it.
 * That is not timidity — it is the same separation core/batch_assign.py and
 * blueprints/smart_match.py both keep between recommending and assigning. A
 * planner that went straight to dispatching would be asking a dispatcher to
 * trust an optimisation they had never been shown.
 *
 * THE BASELINE IS RENDERED, ALWAYS. "Saves 40 minutes" that nobody can
 * check is a marketing number; the greedy figure beside it is what makes it
 * a measurement. And on a batch where planning wins nothing, the panel says
 * exactly that rather than hiding the run — a tool that only ever reports
 * its wins is one a dispatcher learns to discount.
 *
 * It renders nothing at all when there is nothing to plan. A dispatcher
 * whose book is empty should not be looking at a disabled button.
 */
export function AssignmentPlanPanel() {
  const t = useT();
  const batch = useBatchAssign();
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState('');

  const run = (apply) => {
    setError('');
    batch.mutate(apply ? { apply: true } : {}, {
      onSuccess: setPlan,
      onError: (err) => setError(err.message),
    });
  };

  const rows = plan?.assignments ?? [];
  const saved = plan?.saved_minutes ?? 0;

  return (
    <Panel
      title={t('dispatcher.planTitle')}
      actions={
        <button
          type="button"
          className="btn"
          disabled={batch.isPending}
          onClick={() => run(false)}
        >
          {t('dispatcher.planMorning')}
        </button>
      }
    >
      {error ? <ErrorState>{error}</ErrorState> : null}

      {plan?.applied ? (
        <div role="status" className="text-[11.5px]">
          <p>{t('dispatcher.planApplied', { n: plan.applied_order_ids?.length ?? 0 })}</p>
          {plan.refused?.length ? (
            <p className="mt-1 text-amber">
              {t('dispatcher.planRefused', { n: plan.refused.length })}
            </p>
          ) : null}
        </div>
      ) : plan ? (
        <div className="space-y-2 text-[11.5px]">
          {rows.length === 0 ? (
            <p className="text-dim">{t('dispatcher.planNothing')}</p>
          ) : (
            <>
              {/* The claim and the baseline, together. See the docstring. */}
              <p className={saved > 0 ? 'text-green' : 'text-dim'}>
                {saved > 0
                  ? t('dispatcher.planSaves', { n: saved })
                  : t('dispatcher.planNoGain')}
                {plan.saved_deadhead_km > 0
                  ? ` · ${t('dispatcher.planSavesKm', { n: plan.saved_deadhead_km })}`
                  : ''}
              </p>

              <ul className="space-y-1">
                {rows.map((row) => (
                  <li
                    key={row.order_id}
                    className="flex flex-wrap justify-between gap-2 rounded-[9px] border border-line bg-white/2 px-2.5 py-1.5"
                  >
                    <span className="font-semibold">#{row.order_id.slice(0, 6)}</span>
                    <span className="text-dim">
                      {t('dispatcher.planRow', {
                        driver: row.full_name,
                        km: row.deadhead_km ?? '—',
                        n: row.score_minutes ?? '—',
                      })}
                    </span>
                  </li>
                ))}
              </ul>

              {plan.unassigned?.length ? (
                <p className="text-dim">
                  {t('dispatcher.planUnassigned', { n: plan.unassigned.length })}
                  {' — '}
                  {t(`dispatcher.planReason.${plan.unassigned[0].reason}`)}
                </p>
              ) : null}

              <button
                type="button"
                className="btn btn-primary"
                disabled={batch.isPending}
                onClick={() => run(true)}
              >
                {t('dispatcher.planApply')}
              </button>
            </>
          )}
        </div>
      ) : null}
    </Panel>
  );
}
